import { useField } from 'formik';
import styled from 'styled-components';
import type { ReactNode, InputHTMLAttributes, FocusEvent } from 'react';

import { CheckIcon } from '@yoweb/ui/components/Icon/CheckIcon';
import { PassThroughIcon } from '@yoweb/ui/components/Icon/PassThroughIcon';
import { Text } from '@yoweb/ui/components/typography';
import { pxToRem } from '@yoweb/ui/styles/utils';
import { getColor, getSpace, getRadii } from '@yoweb/ui/styles/utils/theme';

type CheckboxProps = {
  name: string;
  type?: string;
  id?: string;
  description?: string;
  value?: number | string;
  partialChecked?: boolean;
  children?: ReactNode;
} & InputHTMLAttributes<HTMLInputElement>;

type CheckboxUIProps = InputHTMLAttributes<HTMLInputElement> & CheckboxProps;

/**
 * A Formik controlled Checkbox component.
 */
const Checkbox = (props: CheckboxProps) => {
  const [field] = useField({
    type: props.type || 'checkbox',
    ...props,
  });

  /**
   * Components that use checkbox might have their own event handlers
   * we need to call both them and the formik event handlers.
   */
  const onChange = (event: FocusEvent<HTMLInputElement>) => {
    props.onChange?.(event);
    field.onChange(event);
  };

  return <CheckboxUI {...field} {...props} onChange={onChange} />;
};

/**
 * A checkbox component.
 */
export const CheckboxUI = ({
  children,
  description,
  type = 'checkbox',
  partialChecked = false,
  ...props
}: CheckboxUIProps) => (
  <Wrap htmlFor={props.id} isDisabled={props.disabled || false}>
    <HiddenCheckbox {...props} type={type} checked={props.checked || partialChecked} />
    <Box>
      <>
        {partialChecked && <PartialCheckMark />}
        {!partialChecked && <CheckMark checked={props.checked} />}
      </>
    </Box>
    <ContentWrap>
      {children && <Text as="span">{children}</Text>}
      {description && (
        <Text as="span" variant="muted">
          {description}
        </Text>
      )}
    </ContentWrap>
  </Wrap>
);

const Wrap = styled.label<{ isDisabled: boolean }>`
  display: flex;
  align-items: baseline;
  position: relative;

  ${({ isDisabled }) => !isDisabled && 'cursor: pointer;'}
`;

const ContentWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const boxMargin = pxToRem(10);

const Box = styled.div`
  background-color: ${getColor('base000')};
  border: 1px solid;
  border-color: ${getColor('base400')};
  border-radius: ${getRadii('tiny')};
  flex-shrink: 0;
  pointer-events: none;
  position: relative;
  transform: translateY(25%);
  transition:
    background-color 100ms,
    box-shadow 100ms;
  margin-right: ${boxMargin};
  height: ${getSpace('normal2')};
  width: ${getSpace('normal2')};
`;

const PartialCheckMark = styled(PassThroughIcon)`
  && {
    color: ${getColor('base000')};
    left: 50%;
    position: absolute;
    top: 50%;
    transition:
      transform 100ms,
      opacity 100ms;
    transform: translate(-50%, -50%) scale(0.75);
  }
`;

const CheckMark = styled(CheckIcon)<{ checked?: boolean }>`
  && {
    color: ${getColor('base000')};
    left: 50%;
    position: absolute;
    top: 50%;
    transition:
      transform 100ms,
      opacity 100ms;
    transform: translate(-50%, -50%) scale(0.75);
    opacity: ${({ checked }) => (checked ? 1 : 0)};
  }
`;

const HiddenCheckbox = styled.input`
  height: 0;
  width: 0;
  opacity: 0;
  position: absolute;

  &:disabled {
    & + ${Box} {
      background-color: ${getColor('base050')};
      border-color: ${getColor('base200')};
    }

    & + ${/* sc-selector */ Box} > ${CheckMark} {
      color: ${getColor('base200')};
    }
  }

  &:not(:disabled):active + ${/* sc-selector */ Box}, &:not(:disabled):hover + ${Box} {
    border-color: ${getColor('base1000')};
  }

  &:not(:disabled):focus + ${Box} {
    border-color: ${getColor('base400')};
    box-shadow:
      rgb(255 255 255) 0 0 0 0,
      ${getColor('base700')} 0 0 0 5px,
      rgba(0 0 0 / 0%) 0 0 0 0;
  }

  &:not(:disabled):checked {
    & + ${/* sc-selector */ Box} > ${CheckMark} {
      transform: translate(-50%, -50%) scale(1);
    }

    & + ${Box} {
      background-color: ${getColor('base1000')};
      border-color: ${getColor('base1000')};
    }

    &:focus + ${Box} {
      border-color: ${getColor('base1000')};
      box-shadow:
        rgb(255 255 255) 0 0 0 1px,
        ${getColor('base700')} 0 0 0 5px,
        rgba(0 0 0 / 0%) 0 0 0 0;
    }
  }
`;

export default Checkbox;
